import { Component, EventEmitter, Input, OnChanges, Output, SecurityContext } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

/**
 *
 * Confirm Modal Component
 *
 * Input Properties:
 *
 *  confirmButtonText: Text used for the button that triggers a confirmation.
 *  cancelButtonText: Text use for the button that does not trigger a confirmation.
 *  confirmHeader: Text used for the header of the modal.
 *  confirmText: Text used in the modal content section.
 *  modalTestId: Used to set the data-test-id attributes of the modal's and its child components
 *                     (footer, confirm button, etc). Make sure this is as unique as possible to avoid test-breaking
 *                     collisions between two elements having the same `data-test-id`
 *  showCancelButton: Whether the cancel button should be shown. Defaults to true
 *  showConfirmButton: Whether the confirm button should be shown. Defaults to true
 *
 *
 * Output Properties:
 *
 *  confirmHandler: EventEmitter that emits when the user confirms or cancels
 *
 */

@Component({ standalone: false, selector: 'ycn-confirm-modal', templateUrl: './confirm-modal.template.html' })
export class ConfirmModalComponent implements OnChanges {
    @Input() confirmButtonText?: string;

    @Input() cancelButtonText?: string;

    @Input() confirmHeader!: string;

    @Input() confirmText!: string;

    @Input() confirmTextClass?: string = '';

    @Input() showCancelButton: boolean = true;

    @Input() showConfirmButton: boolean = true;

    @Input() modalTestId?: string;

    @Input() confirmButtonClasses?: string | string[] | Set<string> | { [p: string]: any } | null = 'btn-primary';

    @Output() closeModalHandler = new EventEmitter<void>();

    @Output() confirmHandler = new EventEmitter<boolean>();

    sanitizedText: SafeHtml = '';

    modalOpen: boolean;

    constructor(private sanitizer: DomSanitizer) {
        this.modalOpen = false;
    }

    ngOnChanges(): void {
        this.sanitizedText = this.sanitizer.sanitize(SecurityContext.HTML, this.confirmText) ?? '';
    }

    openModal(): void {
        this.modalOpen = true;
    }

    closeModal(): void {
        this.modalOpen = false;
        this.closeModalHandler.emit();
    }

    submit(confirmed: boolean): void {
        this.confirmHandler.emit(confirmed);
        this.closeModal();
    }
}
